import React, { useRef, useState } from 'react';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import CheckCircle from 'icons/check-circle.svg';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import Select, { components } from 'react-select';

import useTrackedPersonnel from 'components/hooks/useTrackedPersonnel';

import { DropdownIndicator } from 'components/application/CollectionSelect';
import Tooltip from 'components/application/Tooltip';

import PersonnelCourseTrainingStatusIndicator from 'components/personnel/PersonnelCourseTrainingStatusIndicator';

import { formatDate } from 'components/helpers/dates';
import {
  formatInfiniteData,
  getNextPageParam,
} from 'components/helpers/reactQuery';
import { resourceShape } from 'components/helpers/serialisableResources';
import { personDisplayName } from 'components/helpers/users';

export default function SelectMembers({
  domainBookingSource,
  domainCourse,
  domainPersonnelCollection,
  isELearningAllowanceError,
  isFetching,
  onBookingGroupMemberSelect,
  selectedMemberIds,
}) {
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [isValueMenuOpen, setIsValueMenuOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const {
    isTrackedPersonnelUsageLoaded,
    trackedPersonnelAllowance,
    trackedPersonnelUsage,
  } = useTrackedPersonnel();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'bookings.SelectMembers',
  });

  const { t: i18nTCommon } = useTranslation('common');

  // handlers
  const handleMenuOpen = () => setIsValueMenuOpen(true);
  const handleMenuClose = () => setIsValueMenuOpen(false);

  const handleDebounce = (searchText) => {
    setSearchText(searchText);
    setIsDebouncing(false);
  };

  const debounceInput = useRef(
    _.debounce((searchText) => handleDebounce(searchText), 500),
  ).current;

  const handleInputChange = (searchText) => {
    if (searchText && searchText.length > 0) setIsDebouncing(true);
    debounceInput(searchText);
  };

  // queries
  const {
    data: assignableBookees,
    fetchNextPage: fetchAssignableBookees,
    hasNextPage: assignableBookeesHasNextPage,
    isFetching: assignableBookeesIsFetching,
    isSuccess: assignableBookeesIsSuccess,
  } = useInfiniteQuery({
    queryKey: [
      'assignableBookees',
      'courses',
      domainCourse.id,
      { search: searchText },
    ],
    queryFn: async ({ pageParam = 1 }) => {
      const assignableBookeesResponse = await axios.get(
        `/courses/${domainCourse.id}/assignable_bookees`,
        { params: { page: pageParam, search: searchText } },
      );
      return assignableBookeesResponse.data;
    },
    getNextPageParam,
    select: (data) =>
      formatInfiniteData({
        data,
        resourceTypes: ['booking', 'training'],
      }),
    enabled: !!domainCourse.id && isValueMenuOpen,
  });

  const { data: assignableBookeesMeta } = useQuery({
    queryKey: ['assignableBookeesMeta', 'courses', domainCourse.id],
    queryFn: async () => {
      const assignableBookeesResponse = await axios.get(
        `/courses/${domainCourse.id}/assignable_bookees`,
        { params: { meta_only: true } },
      );
      return assignableBookeesResponse.data.meta;
    },
    enabled: !!domainCourse.id,
  });

  // computed
  const selectedMemberIdsSet = new Set(selectedMemberIds);

  const assignmentBlockers = (function calculateMemberAssignmentBlocker() {
    const blockers = [];
    if (isFetching) {
      blockers.push('fetchingDomainPersonnel');
    }
    if (assignableBookeesMeta?.totalPages === 0) {
      blockers.push('noAssignableBookees');
    }
    if (isELearningAllowanceError) {
      blockers.push('problemDisplayingELearningCredits');
    }

    if (trackedPersonnelAllowance) {
      if (!isTrackedPersonnelUsageLoaded) {
        blockers.push('unableToCalculateTrackedPersonnel');
      }

      (function ensureProposedTrackedPersonnelUsage() {
        const nonPersistedMembers =
          domainPersonnelCollection ?
            selectedMemberIds.filter(
              (personnelId) =>
                !domainPersonnelCollection.some(
                  (existingMember) => existingMember.id === personnelId,
                ),
            )
          : selectedMemberIds;
        if (nonPersistedMembers.length > 0 && !assignableBookeesIsSuccess) {
          blockers.push('unableToCalculateTrackedPersonnel');
          return;
        }
        const personnelWhoWouldBecomeTrackedCount = (() => {
          if (nonPersistedMembers.length > 0) {
            const assignableMembersMap = new Map(
              assignableBookees.data.map((member) => [member.id, member]),
            );
            const nonTrackedUnpersistedMembers = nonPersistedMembers.filter(
              (personnelId) =>
                !assignableMembersMap.get(personnelId)?.meta?.isTracked,
            );
            return nonTrackedUnpersistedMembers.length;
          } else {
            return 0;
          }
        })();

        const personnelWhoWouldBecomeUntrackedCount =
          domainPersonnelCollection ?
            domainPersonnelCollection.filter(
              (personnel) =>
                !selectedMemberIdsSet.has(personnel.id) &&
                personnel.meta.isTrackedOnlyForThisBookingSource,
            ).length
          : 0;

        const currentProposedUsage =
          trackedPersonnelUsage +
          personnelWhoWouldBecomeTrackedCount -
          personnelWhoWouldBecomeUntrackedCount;
        if (currentProposedUsage >= trackedPersonnelAllowance)
          blockers.push('trackedPersonnelLimitReached');
      })();
    }
    return blockers;
  })();

  const hardBlockers = assignmentBlockers.filter((blocker) =>
    [
      'fetchingDomainPersonnel',
      'unableToCalculateTrackedPersonnel',
      'problemDisplayingELearningCredits',
      'noAssignableBookees',
    ].includes(blocker),
  );

  const assignableOptions = (() => {
    if (isDebouncing || !assignableBookeesIsSuccess) return [];

    const isProhibitedFromAddingNonTracked = assignmentBlockers.includes(
      'trackedPersonnelLimitReached',
    );

    return assignableBookees.data.map((member) => {
      const training =
        assignableBookees.included.training[
          member.relationships.latestCourseTraining?.data?.id
        ];

      const booking =
        assignableBookees.included.booking[
          member.relationships.courseBookings?.data?.[0]?.id
        ];

      let disabledReason;

      if (selectedMemberIdsSet.has(member.id)) {
        disabledReason = 'bookedAsPartOfConnection';
      } else if (
        booking &&
        // new connection
        (!domainBookingSource ||
          // not within same domain connection for booking group
          (domainBookingSource.type === 'bookingGroup' &&
            booking?.relationships.bookingGroup?.data?.id !==
              domainBookingSource.id) ||
          // not within same domain connection for booking
          (domainBookingSource.type === 'booking' &&
            booking.id !== domainBookingSource.id))
      ) {
        disabledReason = 'bookedOutsideOfConnection';
      } else if (isProhibitedFromAddingNonTracked && !member.meta.isTracked) {
        disabledReason = 'wouldGoOverTrackedLimit';
      } else if (
        assignmentBlockers.includes('unableToCalculateTrackedPersonnel')
      ) {
        disabledReason = 'unableToCalculateTrackedPersonnel';
      } else if (
        !!domainCourse?.relationships?.eLearningCourse?.data?.id &&
        !member.attributes.email
      ) {
        disabledReason = 'emailRequiredForEnrolment';
      } else if (!member.meta.isBookingEligible) {
        disabledReason = 'lacksBookingPermission';
      }

      return {
        disabled: !!disabledReason,
        label: personDisplayName({ attributes: member.attributes }),
        optionProps: {
          booking,
          course: domainCourse,
          disabledReason,
          member,
          training,
        },
        value: member.id,
      };
    });
  })();

  const disabledTooltip = (() => {
    const hardBlocker = hardBlockers[0];
    if (!hardBlocker) return null;

    if (hardBlocker === 'fetchingDomainPersonnel') {
      return i18nTCommon('shared.loading...');
    } else if (hardBlocker === 'problemDisplayingELearningCredits') {
      return i18nTCommon('bookings.errors.creditInfoUnavailable');
    } else {
      return i18nT(`disabledTooltip.${hardBlocker}`);
    }
  })();

  return (
    <>
      <label
        className={`collection-select__label tw-font-medium after:tw-absolute after:tw-right-0 after:tw-font-normal after:tw-text-grey-500 after:tw-content-['${i18nTCommon('shared.required')}']`}
        htmlFor='bookingGroupMemberIds'
      >
        {i18nTCommon('personnel.addPersonnel')}
      </label>
      <div className='tooltip-parent'>
        {disabledTooltip && (
          <Tooltip
            arrow={true}
            placement='bottom'
            tooltip={disabledTooltip}
            trigger='hover'
          />
        )}
        <Select
          className='collection-select__select-container collection-select--grey-disabled tw-mb-4'
          classNamePrefix='collection-select'
          components={{ DropdownIndicator, Option }}
          filterOption={() => true}
          id='bookingGroupMemberIds'
          isDisabled={hardBlockers.length > 0}
          isLoading={isDebouncing || assignableBookeesIsFetching}
          isOptionDisabled={(option) => option.disabled}
          loadingMessage={() => i18nTCommon('shared.loading...')}
          name='bookingGroupMemberIds'
          noOptionsMessage={() =>
            searchText ?
              <Trans
                components={{
                  a: (
                    /* eslint-disable jsx-a11y/anchor-has-content */
                    <a
                      className='app-link tw-align-baseline tw-text-blue-500 hover:tw-text-blue-300 active:tw-text-blue-300'
                      href='https://intercom.help/hands-hq/en/articles/10248439-new-bulk-actions-mass-book-and-record-training#h_c975352937'
                      rel='noopener noreferrer'
                      target='_blank'
                    />
                    /* eslint-enable jsx-a11y/anchor-has-content */
                  ),
                }}
                i18nKey='bookings.SelectMembers.select.noOptionsMessage'
                ns='components'
              />
            : null
          }
          onChange={(e) => onBookingGroupMemberSelect(e.value)}
          onInputChange={handleInputChange}
          onMenuClose={handleMenuClose}
          onMenuOpen={handleMenuOpen}
          onMenuScrollToBottom={() => {
            if (assignableBookeesHasNextPage) fetchAssignableBookees();
          }}
          openMenuOnFocus={false}
          options={assignableOptions}
          placeholder={i18nT('select.placeholder')}
          value={null}
        />
      </div>
    </>
  );
}

function Option(props) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'bookings.SelectMembers',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const { booking, course, disabledReason, member, training } =
    props.data.optionProps;

  const courseHasELearningCourse =
    !!course?.relationships?.eLearningCourse?.data?.id;

  const additionalText = (() => {
    switch (true) {
      case disabledReason === 'bookedAsPartOfConnection':
        return (
          <div className='tw-flex'>
            <div>{i18nT('Option.additionalText.addedToBooking')}</div>
            <div className='tw-ml-1 tw-pt-0.5'>
              <CheckCircle height={16} width={16} />
            </div>
          </div>
        );
      case disabledReason === 'bookedOutsideOfConnection':
        return (
          <div>
            {courseHasELearningCourse ?
              i18nT('Option.additionalText.enrolledOn', {
                date: formatDate({
                  date: booking.attributes.createdAt,
                  format: 'd MMM yyyy',
                }),
              })
            : i18nTCommon('bookings.bookedFor', {
                date: formatDate({
                  date: booking.attributes.date,
                  format: 'd MMM yyyy',
                }),
              })
            }
          </div>
        );
      case course.attributes.expires:
        switch (member.attributes.trainingStatus) {
          case 0:
          case 1:
          case 5:
            return training ? '' : i18nTCommon('shared.missing');
          case 2:
            return i18nTCommon('shared.expired');
          case 3:
          case 4:
            return i18nT('Option.additionalText.expiresOn', {
              date: formatDate({
                date: training.attributes.expiryDate,
                format: 'd MMM yyyy',
              }),
            });
          default:
            return;
        }
      case !course.attributes.expires:
        return training ?
            i18nTCommon('shared.doesNotExpire')
          : i18nTCommon('shared.missing');
    }
  })();

  const disabledTooltip = (() => {
    if (disabledReason === 'wouldGoOverTrackedLimit') {
      return i18nTCommon('training.reachedLimit');
    } else if (
      [
        'emailRequiredForEnrolment',
        'lacksBookingPermission',
        'unableToCalculateTrackedPersonnel',
      ].includes(disabledReason)
    ) {
      return i18nT(`disabledTooltip.${disabledReason}`);
    }
  })();

  return (
    <components.Option
      {...props}
      className={classNames(
        disabledReason && 'tw-bg-grey-025 tw-text-grey-400',
      )}
    >
      <div className='collection-select__option_container tooltip-parent tw-flex tw-min-h-5 tw-justify-between'>
        {disabledTooltip && (
          <Tooltip
            arrow={true}
            placement='bottom'
            tooltip={disabledTooltip}
            trigger='hover'
          />
        )}
        <div className='tw-inline-flex tw-flex-1'>
          <div className='tw-mt-[5px] tw-self-start'>
            <PersonnelCourseTrainingStatusIndicator
              status={member.attributes.trainingStatus}
            />
          </div>
          <div className='tw-ml-2'>
            <div className='tw-font-medium'>
              {personDisplayName({ attributes: member.attributes })}
            </div>
            <div className='tw-self-center tw-text-s tw-text-grey-500'>
              {member.attributes.externalId}
            </div>
          </div>
        </div>
        <div className='tw-ml-1 tw-whitespace-nowrap'>{additionalText}</div>
      </div>
    </components.Option>
  );
}

SelectMembers.propTypes = {
  selectedMemberIds: PropTypes.array.isRequired,
  domainBookingSource: resourceShape(['booking', 'bookingGroup']),
  domainPersonnelCollection: PropTypes.arrayOf(resourceShape('personnel')),
  domainCourse: resourceShape('course').isRequired,
  onBookingGroupMemberSelect: PropTypes.func.isRequired,
  isFetching: PropTypes.bool,
  isELearningAllowanceError: PropTypes.bool,
};
